import React from 'react';
import api from 'api';
import { Spinner, UnsavedObjectModal } from 'hyperdb-utils';
import { AlertMessage } from 'hyperdb-utils';
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE } from '../../config';

export default function(Component, {
  namespace, mountedUrl, titleNew, titleEdit, displayName, onSubmit = null, AdditionalHeaders = null,
  afterCreate = null, afterUpdate = null, asWizardForCreate = false, maxSteps = 1, backUrl = null, displaySaveButton = true,
  defaultLanguage = DEFAULT_LANGUAGE || 'en', availableLanguages = AVAILABLE_LANGUAGES || ['en'],
  checkIsValid = null, customApi = null,
}) {
  class HyperdbForm extends React.Component {
    constructor(props) {
      super(props);
      this.api = this.props.api[namespace];
      this.handleClickCancel = this.handleClickCancel.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.stayOnForm = this.stayOnForm.bind(this);
      this.leave = this.leave.bind(this);
      this.saveAndLeave = this.saveAndLeave.bind(this);
      this.isNew = !this.props.params.id;
      this.displayName = null;
    }
    componentDidMount() {
      if (this.isNew) this.api.initCreateForm();
      else this.api.initEditForm(this.props.params.id);
      if (asWizardForCreate) this.props.api[namespace].setData({ step: 1 });
    }
    componentWillReceiveProps(nextProps) {
      if (nextProps.formLoaded) {
        this.displayName = displayName ? displayName(nextProps.objectData, nextProps) : '';
        this.displayName = this.displayName && typeof this.displayName === 'object'
          ? (this.displayName.$i18n || {})[defaultLanguage] || ''
          : this.displayName || '';
      }
      if (nextProps.submitSuccessful && !this._leaveAfterSubmit) {
        if (!this.props.objectData.id) { // After create
          if (afterCreate) return afterCreate(nextProps);
          this.props.api.routing.redirect(this.props.router, mountedUrl);
        } else { // After update
          if (afterUpdate) return afterUpdate(nextProps);
          this.api.setData({ submitSuccessful: false, formChanged: false });
        }
      }
    }
    componentWillUnmount() {
      this.api.leaveForm();
    }
    render() {
      if (!this.props.formLoaded) return <div className="box box-primary"><Spinner show={ true } /></div>;
      const formValid = checkIsValid ? checkIsValid(this.props) : true;
      const saveButton = !asWizardForCreate || (this.props.step === maxSteps && maxSteps !== 1)
        ? <button type="button" className="btn btn-sm btn-success" onClick={ this.handleSubmit } disabled={ !this.props.formChanged || this.props.loading || !formValid }>Save</button>
        : <button type="button" className="btn btn-sm btn-primary" onClick={ this.handleSubmit } disabled={ !this.props.formChanged || this.props.loading || !formValid }>
            Next <i className="fas fa-chevron-right" />
          </button>;
      return (
        <div className="content-wrapper">
          <section className="content-header content-header-fixed">
            <div className="row">
              <div className="col-md-11">
                <span className="btn btn-sm btn-primary" onClick={ this.handleClickCancel }>
                  <i className="fas fa-chevron-left"></i> Back
                </span>
                &nbsp;<h3 className="box-title">{ this.isNew ? `${titleNew} ${ this.displayName }` : `${titleEdit} ${ this.displayName }` }</h3>
                {
                  AdditionalHeaders
                    ? <AdditionalHeaders { ...this.props } />
                    : null
                }
              </div>
              {
                displaySaveButton
                  ? <div className="col-md-1">
                      { saveButton }
                    </div>
                  : null
              }
            </div>
          </section>
          <section className="content content-fixed">
            {
              this.props.successMessage
                ? <AlertMessage
                    type="success"
                    onDismiss={ this.api.dismissAlert }>
                      { this.props.successMessage }
                  </AlertMessage>
                : null
            }
            {
              this.props.errorMessage
                ? <AlertMessage
                    type="error"
                    onDismiss={ this.api.dismissAlert }>
                      { this.props.errorMessage }
                  </AlertMessage>
                : null
            }
            <div className="box box-primary">
              <Spinner show={ this.props.loading } />
              <div className="box-body hyperdb-form">
                <form role="form" className="hyperdb-form">
                  <Component
                    { ...this.props }
                    updateEditedObject={ this.api.updateEditedObject }
                    availableLanguages={ availableLanguages }
                    defaultLanguage={ defaultLanguage } />
                </form>
              </div>
            </div>
          </section>
          {
            this.props.showUnsavedObjectModal
              ? <UnsavedObjectModal
                  onClose={ this.stayOnForm }
                  loading={ this.props.loading }
                  onClickCancel={ this.stayOnForm }
                  onClickDiscard={ this.leave }
                  onClickSave={ this.saveAndLeave }
                >
                  <div>Some changes are not yet saved. What do you want to do?</div>
                </UnsavedObjectModal>
              : null
          }
        </div>
      );
    }

    handleSubmit() {
      this.api.setData({ showUnsavedObjectModal: false });
      if (asWizardForCreate && this.props.step < maxSteps) {
        return this.api.setData({ step: this.props.step + 1 });
      }
      if (onSubmit) return onSubmit(this.props);
      return this.isNew
        ? this.api.submitCreateForm(this.props.objectData)
        : this.api.submitEditForm({ id: this.props.params.id, objectData: this.props.objectData });
    }

    handleClickCancel() {
      if (!asWizardForCreate || this.props.step === 1) {
        if (this.props.formChanged) {
          this.api.setData({ showUnsavedObjectModal: true });
        } else {
          this.leave();
        }
      }
      if (asWizardForCreate && this.props.step > 1) {
        this.api.setData({ step: this.props.step - 1 });
      }
    }
    leave() {
      if (backUrl) {
        const url = backUrl(this.props);
        this.props.api.routing.redirect(this.props.router, url);
      } else {
        if (window.previousLocation) {
          this.props.router.push(window.previousLocation);
        } else {
          this.props.api.routing.redirect(this.props.router, mountedUrl);
        }
      }
    }
    stayOnForm() {
      this.api.setData({ showUnsavedObjectModal: false });
    }
    saveAndLeave() {
      this._leaveAfterSubmit = true;
      this.handleSubmit()
        .then(() => this.leave());
    }
  }
  HyperdbForm.propTypes = {
    api: React.PropTypes.object,
    params: React.PropTypes.object,
    router: React.PropTypes.object,
    formLoaded: React.PropTypes.bool,
    objectData: React.PropTypes.object,
    loading: React.PropTypes.bool,
    onSubmit: React.PropTypes.func,
  };

  const _api = !customApi ? api : customApi;
  return _api.connect(HyperdbForm, namespace);
}



// WEBPACK FOOTER //
// ./src/frontend/hyperdb/HyperdbForm.jsx